import React, { useEffect, useState } from "react"
import moment from "moment"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import { Col, Form, Row } from "reactstrap"
import { useParams } from "react-router-dom"
import ReactDatePicker from "react-datepicker"

import {
   AddProductionLine,
   GetProductionLineDetails,
   UpdateProductionLine,
} from "api/api.service"
import { Button } from "components/Button"
import FormikErrorText from "components/FormikErrorText/FormikErrorText"
import FormSkeleton from "components/FormSkeleton/FormSkeleton"
import { Modal } from "components/Modal"
import { TextInput } from "components/TextInput"
import { ConfiguratorPackagesStatus } from "constants"
import ActiveInactiveSwitch from "pages/Configurator/CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import {
   FIELDS_NAME,
   initialValues,
   getValidationSchema,
   TOMORROW_DATE,
} from "../FormConfig"

import calendarIcon from "assets/images/icons/calendar.svg"

const TITLES = {
   MODAL_ADD_TITLE: "New Production Line",
   MODAL_UPDATE_TITLE: "Update Production Line",
   MODAL_DETAILS_TITLE: "Production Line Details",
   ADD_BTN_TEXT: "Add New Production Line",
   UPDATE_BTN_TEXT: "Update Production Line",
   DETAILS_BTN_TEXT: "Done",
}

const handleUpdateExecution = async (id, data) => {
   let res = await UpdateProductionLine(id, data)
   toast.success("Production Line has been updated successfully !")
}

const handleStatusConversion = status => {
   return status == ConfiguratorPackagesStatus.Active ? true : false
}

const ManageProductionLine = ({
   isDetailView = false,
   isOpen = false,
   onToggleModal = () => {},
   onSuccess = () => {},
   id = "",
}) => {
   const { trailerId } = useParams()
   const [isLoading, setIsLoading] = useState(false)
   const [status, setStatus] = useState(false)
   const [isFetching, setIsFetching] = useState(false)

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
         ...initialValues,
      },
      validationSchema: getValidationSchema(id),
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   useEffect(() => {
      handleFetchDetails()
      return () => {
         setIsLoading(false)
      }
   }, [id])

   const handleSubmit = values => {
      let bodyData = {
         name: values[FIELDS_NAME.NAME],
         startDate: convertDateTime({
            date: moment(values[FIELDS_NAME.START_DATE]).startOf("day"),
            customFormat: dateTimeFormat.apiDateTimeFormat,
         }),
         endDate: values[FIELDS_NAME.END_DATE]
            ? convertDateTime({
                 date: moment(values[FIELDS_NAME.END_DATE]).endOf("day"),
                 customFormat: dateTimeFormat.apiDateTimeFormat,
              })
            : null,
         status: values[FIELDS_NAME.STATUS],
         startingSlotId: values[FIELDS_NAME.SLOT_SEQUENCE_NUMBER],
      }

      if (id) {
         handleUpdate(bodyData)
      } else {
         handleAdd(bodyData)
      }
   }

   const handleUpdate = async data => {
      const payload = {
         ...data,
      }

      try {
         setIsLoading(true)
         await handleUpdateExecution(id, payload)
         handleModalClose()
         onSuccess()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }
   const handleAdd = async data => {
      try {
         setIsLoading(true)
         await AddProductionLine(data)
         toast.success("Production Line has been added successfully !")
         onSuccess()
         handleModalClose()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }

   const handleFetchDetails = async () => {
      if (!id) return

      try {
         setIsFetching(true)
         let res = await GetProductionLineDetails(id)

         formik.setValues({
            [FIELDS_NAME.NAME]: res?.name,
            [FIELDS_NAME.START_DATE]: res?.startDate,
            [FIELDS_NAME.END_DATE]: res?.endDate,
            [FIELDS_NAME.STATUS]: res?.status,
            [FIELDS_NAME.SLOT_SEQUENCE_NUMBER]: res?.startingSlotId ?? 0,
         })
         setStatus(handleStatusConversion(res?.status))
      } finally {
         setIsFetching(false)
      }
   }

   const handleModalClose = () => {
      setStatus(false)
      formik.resetForm()
      onToggleModal()
   }
   const handleSetStatus = evt => {
      let { name } = evt.target
      let alteredStatus = !status
      setStatus(alteredStatus)
      formik.setFieldValue(
         name,
         alteredStatus
            ? ConfiguratorPackagesStatus.Active
            : ConfiguratorPackagesStatus.Inactive
      )
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container justify-content-center mb-0"
         sizeClasses="2"
         backdrop="static"
         modalTitle={id ? TITLES.MODAL_UPDATE_TITLE : TITLES.MODAL_ADD_TITLE}
      >
         {isFetching ? (
            <div>
               <FormSkeleton />
            </div>
         ) : (
            <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
               <Row>
                  <Col xs={12}>
                     <Form onSubmit={formik.handleSubmit}>
                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Name
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter Name"
                                 name={FIELDS_NAME.NAME}
                                 {...formik.getFieldProps(FIELDS_NAME.NAME)}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.NAME}
                              />
                           </div>{" "}
                        </Row>
                        {!id && (
                           <>
                              <Row className="mt-4 mb-3 align-items-center">
                                 <label className=" col-md-3 col-form-label clr-theme-primary">
                                    Start Date
                                 </label>
                                 <div className="col-md-9 ">
                                    {isDetailView ? (
                                       <small>
                                          {publishingDate
                                             ? publishingDate instanceof Date
                                                ? publishingDate.toLocaleDateString()
                                                : publishingDate
                                             : "-"}
                                       </small>
                                    ) : (
                                       <>
                                          <div className="position-relative">
                                             <ReactDatePicker
                                                autoComplete="off"
                                                dateFormat="MM/dd/yyyy"
                                                showTimeInput={false}
                                                calendarIcon={calendarIcon}
                                                selected={
                                                   formik.values[
                                                      FIELDS_NAME.START_DATE
                                                   ]
                                                      ? new Date(
                                                           formik.values[
                                                              FIELDS_NAME.START_DATE
                                                           ]
                                                        )
                                                      : ""
                                                }
                                                placeholderText="Select Start Date"
                                                minDate={TOMORROW_DATE}
                                                name={FIELDS_NAME.START_DATE}
                                                onChange={date =>
                                                   formik.setFieldValue(
                                                      FIELDS_NAME.START_DATE,
                                                      date
                                                   )
                                                }
                                                className={`custom-date-picker ${
                                                   isDetailView &&
                                                   "input-readonly"
                                                }`}
                                             />
                                             <img
                                                src={calendarIcon}
                                                alt="Calendar"
                                                className="calendar-icon"
                                             />
                                          </div>

                                          <FormikErrorText
                                             formikInstance={formik}
                                             fieldName={FIELDS_NAME.START_DATE}
                                          />
                                       </>
                                    )}
                                 </div>
                              </Row>
                           </>
                        )}
                        <Row className="mt-4 mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              End Date
                           </label>
                           <div className="col-md-9 position-relative">
                              {isDetailView ? (
                                 <small>
                                    {publishingDate
                                       ? publishingDate instanceof Date
                                          ? publishingDate.toLocaleDateString()
                                          : publishingDate
                                       : "-"}
                                 </small>
                              ) : (
                                 <>
                                    {" "}
                                    <div className="position-relative">
                                       <ReactDatePicker
                                          autoComplete="off"
                                          dateFormat="MM/dd/yyyy"
                                          showTimeInput={false}
                                          calendarIcon={calendarIcon}
                                          selected={
                                             formik.values[FIELDS_NAME.END_DATE]
                                                ? new Date(
                                                     formik.values[
                                                        FIELDS_NAME.END_DATE
                                                     ]
                                                  )
                                                : ""
                                          }
                                          isClearable={
                                             formik.values[FIELDS_NAME.END_DATE]
                                                ? true
                                                : false
                                          }
                                          placeholderText="Select End Date"
                                          minDate={new Date()}
                                          name={FIELDS_NAME.END_DATE}
                                          onChange={date =>
                                             formik.setFieldValue(
                                                FIELDS_NAME.END_DATE,
                                                date
                                             )
                                          }
                                          className={`custom-date-picker ${
                                             isDetailView && "input-readonly"
                                          }`}
                                       />
                                       <img
                                          src={calendarIcon}
                                          alt="Calendar"
                                          className="calendar-icon"
                                       />
                                    </div>
                                    <FormikErrorText
                                       formikInstance={formik}
                                       fieldName={FIELDS_NAME.END_DATE}
                                    />
                                 </>
                              )}
                           </div>
                        </Row>

                        <Row className="mb-3 align-items-center">
                           <label className=" col-md-3 col-form-label clr-theme-primary">
                              Slot Sequence Number
                           </label>
                           <div className="col-md-9">
                              <TextInput
                                 inputClass={isDetailView && "input-readonly"}
                                 placeholder="Enter Slot Sequence Number"
                                 type="number"
                                 name={FIELDS_NAME.SLOT_SEQUENCE_NUMBER}
                                 {...formik.getFieldProps(
                                    FIELDS_NAME.SLOT_SEQUENCE_NUMBER
                                 )}
                              />
                              <FormikErrorText
                                 formikInstance={formik}
                                 fieldName={FIELDS_NAME.SLOT_SEQUENCE_NUMBER}
                              />
                           </div>
                        </Row>
                        {id ? (
                           <Row className="mb-3 align-items-center">
                              <label className=" col-md-3 col-form-label clr-theme-primary">
                                 Status
                              </label>
                              <div className="col-md-9">
                                 <ActiveInactiveSwitch
                                    isChecked={status}
                                    isDisable={!id ? true : false}
                                    name={FIELDS_NAME.STATUS}
                                    onChange={handleSetStatus}
                                 />
                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.STATUS}
                                 />
                              </div>
                           </Row>
                        ) : (
                           <></>
                        )}

                        <div className="d-flex justify-content-center mt-3 mt-2">
                           <Button
                              isLoading={isLoading}
                              type={"submit"}
                              title={
                                 id
                                    ? TITLES.UPDATE_BTN_TEXT
                                    : TITLES.ADD_BTN_TEXT
                              }
                              className="header-button"
                              onClick={isDetailView && handleModalClose}
                           />
                        </div>
                     </Form>
                  </Col>
               </Row>
            </div>
         )}
      </Modal>
   )
}

export default ManageProductionLine
